'use client';

import { useEffect } from 'react';
import { API_URLS } from '$constants/apis';
import { useFetch } from '@brahma/hooks';
import { getCookiesData, interpolateString, setCookie } from '@brahma/utils';
import { PLATFORM_TYPES } from '@dfg/entity-library/pure-types';
import type { CoBrandPlatformPreferences } from '$types/coBrandPlatformPreferences';
import useGlobalContext from '$hooks/use-global-context';
import { ContextAction } from '$context/global-context/types';
import { Loader } from '@brahma/components';
import { CO_BRAND_CLIENT_ID, CO_BRAND_SUBDOMAIN_COOKIE_NAME, COOKIE_NAME, DEFAULT_LNG } from '$locale/constants';
import type { IBFFResponse } from '@dfg/brahma-types';
import { coBrandDetailsInitial, optionVisibilityInitial } from '$context/global-context/reducers/coBrandDetails';
import type { Theme } from '$context/theme-context';
import { useTheme } from '$context/theme-context';
import useLocale from '$hooks/use-locale';
import { COMMON } from '$locale/constants';
import { getOnboardingWarningMessage } from '$utils/dashboard-items';
const CoBrandDetails: React.FC = () => {
  const {
    dispatch
  } = useGlobalContext();
  const {
    setTheme
  } = useTheme();
  const {
    t
  } = useLocale(COMMON);
  const cookieData = getCookiesData();
  const fullUrl = typeof window !== 'undefined' && window.location.href ? window.location.href : '';
  const encodedUrl = encodeURI(fullUrl);
  const {
    data: coBrandDetailResponse,
    loading: coBrandDetailFetchLoading
  } = useFetch<IBFFResponse<CoBrandPlatformPreferences>>({
    url: interpolateString(API_URLS.getPlatformPreferenceForCoBrandDetails, {
      url: encodedUrl,
      platformType: PLATFORM_TYPES.APPLICANT
    }),
    component: 'CoBrandDetail'
  });
  useEffect(() => {
    if (coBrandDetailResponse?.code === 200) {
      if (coBrandDetailResponse.data && Object.keys(coBrandDetailResponse.data).length) {
        if (coBrandDetailResponse.data?.themeName) setTheme(coBrandDetailResponse.data.themeName as Theme);
        dispatch({
          type: ContextAction.setCoBrandDetails,
          payload: {
            name: coBrandDetailResponse.data?.name,
            logo: coBrandDetailResponse.data?.logo?.mainLogo ?? '',
            smallLogo: coBrandDetailResponse.data?.logo?.minimizedNavbarLogo ?? '',
            lang: coBrandDetailResponse.data?.language?.code?.toLowerCase() ?? '',
            themeName: coBrandDetailResponse.data?.themeName,
            clientId: coBrandDetailResponse.data?.resourceEntityId,
            carousal: coBrandDetailResponse.data?.carousal,
            subDomain: coBrandDetailResponse.data?.subDomain ?? '',
            isLoaded: true,
            excludedAncillarySources: coBrandDetailResponse.data?.ancillaryServicesConfig?.excludedSources ?? [],
            onboardingWarningMessage: getOnboardingWarningMessage(coBrandDetailResponse.data?.onboardingWarningMessage, cookieData[COOKIE_NAME] as string || DEFAULT_LNG),
            optionVisibility: {
              hiddenOptions: coBrandDetailResponse.data?.optionVisibility?.hiddenOptions ?? [],
              isLoaded: true
            }
          }
        });

        // Store clientId in cookie if not already present or different value
        const coBrandClientId = cookieData[CO_BRAND_CLIENT_ID] ?? '';
        if (coBrandDetailResponse.data?.resourceEntityId && coBrandDetailResponse.data?.resourceEntityId !== coBrandClientId) {
          setCookie(CO_BRAND_CLIENT_ID, coBrandDetailResponse.data.resourceEntityId, 365);
        }

        // Store subDomain in cookie if not already present or different value
        const coBrandSubDomain = cookieData[CO_BRAND_SUBDOMAIN_COOKIE_NAME] ?? '';
        if (coBrandDetailResponse.data?.subDomain && coBrandDetailResponse.data.subDomain !== coBrandSubDomain) {
          setCookie(CO_BRAND_SUBDOMAIN_COOKIE_NAME, coBrandDetailResponse.data.subDomain, 365);
        }
      } else {
        setCookie(CO_BRAND_CLIENT_ID, '', 365);
        setCookie(CO_BRAND_SUBDOMAIN_COOKIE_NAME, '', 365);
        setTheme('primary');
        dispatch({
          type: ContextAction.setCoBrandDetails,
          payload: {
            ...coBrandDetailsInitial,
            isLoaded: true,
            optionVisibility: {
              ...optionVisibilityInitial,
              isLoaded: true
            }
          }
        });
      }
    } else {
      setTheme('primary');
      dispatch({
        type: ContextAction.setCoBrandDetails,
        payload: {
          ...coBrandDetailsInitial,
          isLoaded: true,
          optionVisibility: {
            ...optionVisibilityInitial,
            isLoaded: true
          }
        }
      });
    }
  }, [coBrandDetailResponse]);
  return <Loader loaderText={t('loader.text')} show={coBrandDetailFetchLoading} data-sentry-element="Loader" data-sentry-component="CoBrandDetails" data-sentry-source-file="co-brand.tsx" />;
};
export default CoBrandDetails;